import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';
import Input from '../../../components/ui/Input';
import userApprovalService from '../../../services/userApprovalService';
import { useAuth } from '../../../contexts/AuthContext';

const UserApprovalPanel = () => {
  const { userProfile } = useAuth();
  const [approvalRequests, setApprovalRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadApprovalRequests();
  }, []);

  const loadApprovalRequests = async () => {
    try {
      setLoading(true);
      const data = await userApprovalService?.getAllApprovalRequests();
      setApprovalRequests(data);
    } catch (error) {
      console.error('Error loading approval requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    if (!userProfile?.id) {
      alert('يجب تسجيل الدخول كمسؤول');
      return;
    }

    try {
      setLoading(true);
      await userApprovalService?.approveUser(userId, userProfile?.id);
      await loadApprovalRequests();
      alert('تم الموافقة على المستخدم بنجاح');
    } catch (error) {
      console.error('Error approving user:', error);
      alert('فشل في الموافقة على المستخدم: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectClick = (user) => {
    setSelectedUser(user);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason?.trim()) {
      alert('يرجى إدخال سبب الرفض');
      return;
    }

    if (!userProfile?.id) {
      alert('يجب تسجيل الدخول كمسؤول');
      return;
    }

    try {
      setLoading(true);
      await userApprovalService?.rejectUser(selectedUser?.id, userProfile?.id, rejectionReason);
      await loadApprovalRequests();
      setShowRejectModal(false);
      alert('تم رفض المستخدم بنجاح');
    } catch (error) {
      console.error('Error rejecting user:', error);
      alert('فشل في رفض المستخدم: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_approval: { label: 'قيد المراجعة', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
      approved: { label: 'موافق عليه', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
      rejected: { label: 'مرفوض', color: 'bg-red-500/20 text-red-400 border-red-500/30' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending_approval;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const getRoleLabel = (role) => {
    const roleLabels = {
      admin: 'مدير النظام',
      property_manager: 'مدير الخصائص',
      contract_specialist: 'أخصائي العقود',
      client_manager: 'مدير العملاء',
      agent: 'وكيل',
      manager: 'مدير',
      staff: 'موظف'
    };
    return roleLabels?.[role] || role;
  };

  const filteredRequests = approvalRequests?.filter(request => {
    if (filterStatus === 'all') return true;
    return request?.approval_status === filterStatus;
  });

  const pendingCount = approvalRequests?.filter(r => r?.approval_status === 'pending_approval')?.length;
  const approvedCount = approvalRequests?.filter(r => r?.approval_status === 'approved')?.length;
  const rejectedCount = approvalRequests?.filter(r => r?.approval_status === 'rejected')?.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Icon name="UserCheck" size={28} color="#3B82F6" />
            موافقات المستخدمين
          </h2>
          <p className="text-muted-foreground mt-1">
            مراجعة والموافقة على طلبات التسجيل الجديدة
          </p>
        </div>
        <Button
          onClick={loadApprovalRequests}
          variant="outline"
          iconName="RefreshCw"
          disabled={loading}
        >
          تحديث
        </Button>
      </div>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">قيد المراجعة</p>
              <p className="text-2xl font-bold text-yellow-400 mt-1">{pendingCount}</p>
            </div>
            <div className="p-3 bg-yellow-500/20 rounded-lg">
              <Icon name="Clock" size={24} color="#FBBF24" />
            </div>
          </div>
        </div>

        <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">موافق عليهم</p>
              <p className="text-2xl font-bold text-green-400 mt-1">{approvedCount}</p>
            </div>
            <div className="p-3 bg-green-500/20 rounded-lg">
              <Icon name="CheckCircle" size={24} color="#10B981" />
            </div>
          </div>
        </div>

        <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">مرفوضين</p>
              <p className="text-2xl font-bold text-red-400 mt-1">{rejectedCount}</p>
            </div>
            <div className="p-3 bg-red-500/20 rounded-lg">
              <Icon name="XCircle" size={24} color="#EF4444" />
            </div>
          </div>
        </div>
      </div>
      {/* Filter Tabs */}
      <div className="flex gap-2 border-b border-border">
        {[
          { value: 'all', label: 'الكل', count: approvalRequests?.length },
          { value: 'pending_approval', label: 'قيد المراجعة', count: pendingCount },
          { value: 'approved', label: 'موافق عليهم', count: approvedCount },
          { value: 'rejected', label: 'مرفوضين', count: rejectedCount }
        ]?.map(tab => (
          <button
            key={tab?.value}
            onClick={() => setFilterStatus(tab?.value)}
            className={`px-4 py-2 text-sm font-medium transition-colors relative ${
              filterStatus === tab?.value
                ? 'text-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab?.label} ({tab?.count})
            {filterStatus === tab?.value && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
        ))}
      </div>
      {/* Approval Requests List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : filteredRequests?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">لا توجد طلبات</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredRequests?.map(request => (
            <div
              key={request?.id}
              className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-4 hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-primary/20 rounded-lg">
                      <Icon name="User" size={20} color="#3B82F6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{request?.full_name}</h3>
                      <p className="text-sm text-muted-foreground">{request?.email}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                    <div>
                      <p className="text-xs text-muted-foreground">الدور الوظيفي</p>
                      <p className="text-sm font-medium text-white mt-1">
                        {getRoleLabel(request?.role)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">تاريخ التسجيل</p>
                      <p className="text-sm font-medium text-white mt-1">
                        {request?.created_at && new Date(request.created_at)?.toLocaleDateString('ar-SA')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">الحالة</p>
                      <div className="mt-1">
                        {getStatusBadge(request?.approval_status)}
                      </div>
                    </div>
                    {request?.phone && (
                      <div>
                        <p className="text-xs text-muted-foreground">رقم الهاتف</p>
                        <p className="text-sm font-medium text-white mt-1">{request?.phone}</p>
                      </div>
                    )}
                  </div>

                  {request?.rejection_reason && (
                    <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">سبب الرفض:</p>
                      <p className="text-sm text-red-400">{request?.rejection_reason}</p>
                    </div>
                  )}

                  {request?.approved_at && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      تمت المعالجة في: {new Date(request.approved_at)?.toLocaleString('ar-SA')}
                    </div>
                  )}
                </div>

                {request?.approval_status === 'pending_approval' && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleApprove(request?.id)}
                      variant="default"
                      size="sm"
                      iconName="Check"
                      disabled={loading}
                    >
                      موافقة
                    </Button>
                    <Button
                      onClick={() => handleRejectClick(request)}
                      variant="outline"
                      size="sm"
                      iconName="X"
                      disabled={loading}
                    >
                      رفض
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Reject Modal */}
      {showRejectModal && (
        <Modal
          isOpen={showRejectModal}
          onClose={() => setShowRejectModal(false)}
          title="رفض طلب التسجيل"
          icon="XCircle"
          iconColor="#EF4444"
        >
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              هل أنت متأكد من رفض طلب التسجيل لـ <strong className="text-white">{selectedUser?.full_name}</strong>؟
            </p>
            <Input
              label="سبب الرفض"
              placeholder="أدخل سبب رفض الطلب"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e?.target?.value)}
              required
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowRejectModal(false)}
                disabled={loading}
                fullWidth
              >
                إلغاء
              </Button>
              <Button
                onClick={handleRejectConfirm}
                loading={loading}
                disabled={loading || !rejectionReason?.trim()}
                fullWidth
                iconName="X"
              >
                تأكيد الرفض
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default UserApprovalPanel;